import React, { useEffect, useState, useCallback } from "react";
import { apiGet, apiDelete, apiPost, apiUpload, BACKEND_URL } from "@/lib/api";
import { Image as ImageIcon, Search, Upload, FolderInput, Trash2, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TIPURI_PROFIL = [
  { value: "toate", label: "Toate tipurile" },
  { value: "toc_fix", label: "Toc Fix" },
  { value: "toc_mobil", label: "Toc Mobil" },
  { value: "toc_mobil_jos", label: "Toc Mobil Jos (+picurator aluminiu)" },
  { value: "toc_mobil_jos_picurator", label: "Toc Mobil Jos Picurator Lemn" },
  { value: "cercevea", label: "Cercevea" },
  { value: "traversa", label: "Traversa" },
  { value: "montant", label: "Montant" },
  { value: "bagheta", label: "Bagheta" },
  { value: "inversor", label: "Inversor" },
  { value: "montant_vertical", label: "Montant Vertical" },
  { value: "montant_orizontal", label: "Montant Orizontal" },
  { value: "cercevea_usa", label: "Cercevea Usa" },
  { value: "inversor_usa", label: "Inversor Usa" },
];

const ImageLightbox = ({ src, onClose }) => {
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose} data-testid="image-lightbox">
      <div className="relative max-w-[90vw] max-h-[90vh]">
        <button onClick={onClose} className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow-lg hover:bg-slate-100 z-10" data-testid="lightbox-close">
          <X className="h-5 w-5" />
        </button>
        <img src={src} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
      </div>
    </div>
  );
};

export default function ImaginiPage() {
  const { isAdmin } = useAuth();
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("profile_prelucrate");
  const [images, setImages] = useState([]);
  const [search, setSearch] = useState("");
  const [tipProfil, setTipProfil] = useState("toate");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [detail, setDetail] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [targetCategory, setTargetCategory] = useState("");

  const fetchCategories = useCallback(async () => {
    try {
      const data = await apiGet("/images-library/categories");
      setCategories(data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchImages = useCallback(async () => {
    if (!activeCategory) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (tipProfil && tipProfil !== "toate") params.append("tip", tipProfil);
      const data = await apiGet(`/images-library/${activeCategory}?${params}`);
      setImages(data.images || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, search, tipProfil]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchImages(); }, [fetchImages]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      try {
        await apiUpload(`/images-library/${activeCategory}`, file);
        ok++;
      } catch (err) {
        console.error(err);
      }
    }
    setUploading(false);
    if (ok > 0) {
      toast.success(`${ok} imagine(i) incarcate`);
      fetchImages();
      fetchCategories();
    } else {
      toast.error("Incarcarea a esuat");
    }
    e.target.value = "";
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm("Sigur doriti sa stergeti aceasta imagine?")) return;
    try {
      await apiDelete(`/images-library/${activeCategory}/${imageId}`);
      toast.success("Imagine stearsa");
      setDetail(null);
      fetchImages();
      fetchCategories();
    } catch (e) {
      toast.error("Eroare la stergere");
    }
  };

  const openMove = (img) => {
    setMoveTarget(img);
    setTargetCategory("");
  };

  const doMove = async () => {
    if (!moveTarget || !targetCategory || targetCategory === activeCategory) {
      toast.error("Selectati o categorie diferita");
      return;
    }
    try {
      await apiPost(`/images-library/${activeCategory}/${moveTarget.id}/move`, { target_category: targetCategory });
      toast.success("Imagine mutata");
      setMoveTarget(null);
      setDetail(null);
      fetchImages();
      fetchCategories();
    } catch (e) {
      toast.error("Eroare la mutare");
    }
  };

  const currentCount = categories.find((c) => c.slug === activeCategory)?.count || 0;
  const totalImages = categories.reduce((acc, c) => acc + (c.count || 0), 0);
  const currentName = categories.find((c) => c.slug === activeCategory)?.nume || activeCategory;

  return (
    <div className="py-2 space-y-5" data-testid="imagini-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ImageIcon className="h-6 w-6 text-amber-600" />
            Biblioteca de Imagini
          </h1>
          <p className="text-sm text-slate-500">{totalImages} imagini in total</p>
        </div>
        {isAdmin && (
          <label className="cursor-pointer" data-testid="upload-label">
            <input type="file" className="hidden" multiple accept="image/*,.pdf,application/pdf" onChange={handleUpload} disabled={uploading} data-testid="upload-input" />
            <Button size="sm" asChild disabled={uploading}>
              <span data-testid="upload-button"><Upload className="h-4 w-4 mr-1" />{uploading ? "Se incarca..." : "Incarca imagini"}</span>
            </Button>
          </label>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-b pb-3" data-testid="category-tabs">
        {categories.map((cat) => (
          <button
            key={cat.slug}
            data-testid={`category-${cat.slug}`}
            onClick={() => setActiveCategory(cat.slug)}
            className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-colors ${
              activeCategory === cat.slug
                ? "bg-amber-100 text-amber-800 border border-amber-300"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            <span>{cat.nume}</span>
            <span className="bg-white/80 px-1.5 py-0.5 rounded text-xs font-medium">{cat.count || 0}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9 h-9" placeholder="Cauta imagine..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-input" />
        </div>
        {activeCategory === "profile_prelucrate" && (
          <Select value={tipProfil} onValueChange={setTipProfil}>
            <SelectTrigger className="w-56 h-9" data-testid="tip-filter"><SelectValue placeholder="Tip profil" /></SelectTrigger>
            <SelectContent>
              {TIPURI_PROFIL.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4" data-testid="images-grid">
        {loading ? (
          <div className="col-span-full text-center py-10 text-slate-500">
            <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin opacity-50" />
            <p className="text-sm">Se incarca imaginile...</p>
          </div>
        ) : images.length === 0 ? (
          <div className="col-span-full text-center py-10 text-slate-500" data-testid="empty-state">
            <ImageIcon className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nu exista imagini in categoria "{currentName}"</p>
          </div>
        ) : (
          images.map((img) => (
            <div key={img.id} data-testid={`image-card-${img.id}`} className="relative group border rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow cursor-pointer" onClick={() => setDetail(img)}>
              <div className="aspect-square bg-slate-50">
                <img src={`${BACKEND_URL}${img.url}`} alt={img.name || img.filename} className="w-full h-full object-contain p-1" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                <p className="text-white text-xs truncate text-center mb-1">{img.name || img.filename}</p>
                {isAdmin && (
                  <div className="flex justify-center gap-1">
                    <button onClick={(e) => { e.stopPropagation(); openMove(img); }} className="h-6 w-6 bg-white/90 rounded flex items-center justify-center hover:bg-blue-100" title="Muta in alta categorie" data-testid={`move-${img.id}`}>
                      <FolderInput className="h-3 w-3 text-blue-500" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); deleteImage(img.id); }} className="h-6 w-6 bg-white/90 rounded flex items-center justify-center hover:bg-red-100" title="Sterge" data-testid={`delete-${img.id}`}>
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </button>
                  </div>
                )}
              </div>
              <div className="p-1 text-center border-t">
                <p className="text-xs text-slate-600 truncate">{img.name || img.filename}</p>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="text-sm text-slate-500 text-center pt-2 border-t">{currentCount} imagine(i) in {currentName}</div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="detail-dialog">
          <DialogHeader><DialogTitle>{detail?.name || detail?.filename}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 flex items-center justify-center cursor-zoom-in" onClick={() => setLightbox(`${BACKEND_URL}${detail.url}`)}>
                <img src={`${BACKEND_URL}${detail.url}`} alt={detail.name || detail.filename} className="max-w-full max-h-96 object-contain" />
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">Fisier:</span> {detail.filename}</div>
                <div><span className="text-slate-500">Dimensiune:</span> {detail.size ? `${(detail.size / 1024).toFixed(1)} KB` : "-"}</div>
                <div><span className="text-slate-500">Tip:</span> {detail.content_type || "-"}</div>
                <div><span className="text-slate-500">Categorie:</span> {detail.category || activeCategory}</div>
              </div>
            </div>
          )}
          {isAdmin && (
            <DialogFooter>
              <Button variant="outline" onClick={() => openMove(detail)}><FolderInput className="h-4 w-4 mr-1" /> Muta</Button>
              <Button variant="destructive" onClick={() => deleteImage(detail?.id)}>Sterge</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!moveTarget} onOpenChange={(o) => !o && setMoveTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="move-dialog">
          <DialogHeader><DialogTitle>Muta imaginea</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">Selectati categoria destinatie pentru: {moveTarget?.name || moveTarget?.filename}</p>
            <Select value={targetCategory} onValueChange={setTargetCategory}>
              <SelectTrigger data-testid="move-target-select"><SelectValue placeholder="Selectati categoria..." /></SelectTrigger>
              <SelectContent>
                {categories.filter((c) => c.slug !== activeCategory).map((cat) => (
                  <SelectItem key={cat.slug} value={cat.slug}>{cat.nume}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveTarget(null)}>Anuleaza</Button>
            <Button onClick={doMove} disabled={!targetCategory} data-testid="move-confirm">Muta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
